import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const LessonSidebar = ({ moduleId, currentLessonId, courseId }) => {
  const [module, setModule] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLessons, setExpandedLessons] = useState(new Set([currentLessonId]));
  const [lessonPages, setLessonPages] = useState({}); 

  useEffect(() => {
    if (moduleId && courseId) {
      loadModuleData();
    }
  }, [moduleId, courseId]);

  useEffect(() => {
    expandedLessons.forEach(lessonId => {
      if (!lessonPages[lessonId]) {
        loadLessonPages(lessonId);
      }
    });
  }, [expandedLessons]);

  const loadModuleData = async () => {
    try {
      setLoading(true);
      
      const response = await api.get(`/courses/${courseId}/modules/${moduleId}/lessons`);
      
      if (response.data.success) {
        const moduleData = response.data.data.module;
        const lessonsData = response.data.data.lessons || [];
        
        setModule(moduleData);
        setLessons(lessonsData);
      }
    } catch (error) {
      console.error('Error loading module data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLessonPages = async (lessonId) => {
    try {
      const response = await api.get(`/lessons/${lessonId}`);
      if (response.data.success) {
        const pages = response.data.data.pages || [];
        setLessonPages(prev => ({
          ...prev,
          [lessonId]: pages
        }));
      }
    } catch (error) {
      console.error('Error loading lesson pages:', error);
    }
  };

  const toggleLesson = (lessonId) => {
    setExpandedLessons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lessonId)) {
        newSet.delete(lessonId);
      } else {
        newSet.add(lessonId);
      }
      return newSet;
    });
  };

  const getPageDisplayName = (page) => {
    if (page.type === 'content') {
      const firstBlock = page.blocks?.[0];
      if (firstBlock?.type === 'sub-header' && firstBlock?.text?.toLowerCase().includes('introduction')) {
        return 'Introduction';
      }
      if (firstBlock?.text) {
        return firstBlock.text;
      }
      return 'Content';
    } else if (page.type === 'checkpoint-directions') {
      return 'Checkpoint Quiz';
    } else if (page.type === 'quiz-question') {
      return 'Checkpoint Quiz';
    } else if (page.type === 'quiz-results') {
      return 'Checkpoint Quiz';
    } else if (page.type === 'virtual-class') {
      return 'Virtual Class';
    } else if (page.type === 'arc-quiz') {
      return 'ARC Quiz';
    } else if (page.type === 'interactive-grid') {
      return 'Interactive Grid';
    }
    return page.title || 'Page';
  };

  const isPageCompleted = (lessonId, pageIndex) => {
    return false; 
  };

  if (loading) {
    return (
      <div className="w-80 bg-white border-l border-gray-200" style={{ height: 'calc(100vh - 80px)' }}>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-48 bg-gray-200 rounded mb-4"></div>
            <div className="h-6 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 bg-gray-200 rounded mb-4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!module) {
    return null;
  }

  const moduleImageUrl = `https://d3vx0tne0vxeo1.cloudfront.net/teachers/profile/Module%20${module.order}%20pic.png`;

  return (
    <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto" style={{ height: 'calc(100vh - 80px)' }}>
      <div className="p-6">
        {/* Module Header */}
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-1">Module {module.order}</p>
          <h2 className="text-xl font-bold text-primary mb-4">
            {module.title || `Module ${module.order}`}
          </h2>
          
          {/* Module Image */}
          <div className="mb-6 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
            <img
              src={moduleImageUrl}
              alt={module.title || `Module ${module.order}`}
              className="w-full h-auto object-contain"
              style={{ maxHeight: '200px', width: '100%' }}
              onError={(e) => {
                console.error('Module image failed to load:', moduleImageUrl);
                e.target.src = 'https://via.placeholder.com/400x300?text=Module+Image';
              }}
            />
          </div>
        </div>

        <div>
          <div className="bg-primary text-white px-4 py-3 mb-4 rounded-t">
            <h3 className="text-sm font-semibold uppercase tracking-wide mb-1">
              Course Content
            </h3>
            <p className="text-sm font-medium">
              Module {module.order}: {module.title || `Module ${module.order}`}
            </p>
          </div>
          
          <div className="space-y-0 border-t-2 border-gray-300">
            {lessons.map((lesson, index) => {
              const isCurrentLesson = lesson.id === currentLessonId;
              const isExpanded = expandedLessons.has(lesson.id);
              const pages = lessonPages[lesson.id] || [];
              
              const displayPages = pages.map((page, pageIndex) => ({
                ...page,
                displayName: getPageDisplayName(page),
                pageIndex
              }));
              
              const uniquePages = [];
              const seenTypes = new Set();
              displayPages.forEach(page => {
                const key = page.type === 'quiz-question' || page.type === 'quiz-results' || page.type === 'checkpoint-directions' 
                  ? 'checkpoint' 
                  : page.displayName;
                if (!seenTypes.has(key)) {
                  seenTypes.add(key);
                  uniquePages.push(page);
                }
              });
              
              return (
                <div key={lesson.id} className="border-b-2 border-gray-300 last:border-b-0">
                  <div
                    onClick={() => toggleLesson(lesson.id)}
                    className={`px-4 py-3 text-sm cursor-pointer transition-colors ${
                      isCurrentLesson
                        ? 'bg-blue-50 text-primary font-semibold'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="flex-1">
                        Lesson {lesson.globalOrder || lesson.order || index + 1}: {lesson.title || `Lesson ${lesson.globalOrder || lesson.order || index + 1}`}
                      </span>
                      <svg
                        className={`w-4 h-4 transition-transform flex-shrink-0 ${isExpanded ? '' : 'transform rotate-180'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </div>
                  </div>
                  
                  {isExpanded && uniquePages.length > 0 && (
                    <div className="bg-white border-t-2 border-gray-200">
                      {uniquePages.map((page, pageIdx) => {
                        const isCompleted = isPageCompleted(lesson.id, page.pageIndex);
                        const isIntroduction = page.displayName === 'Introduction';
                        const isChecked = isCompleted || (isIntroduction && isCurrentLesson);
                        
                        return (
                          <div key={page.id || pageIdx} className="flex items-center px-4 py-2.5 text-sm text-gray-700 border-b-2 border-gray-200 last:border-b-0">
                            <div className="mr-3 flex-shrink-0">
                              {isChecked ? (
                                <div className="w-4 h-4 bg-primary rounded flex items-center justify-center">
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              ) : (
                                <div className="w-4 h-4 bg-white rounded border-2 border-gray-300"></div>
                              )}
                            </div>
                            <span className="text-gray-700">{page.displayName}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonSidebar;
